import { useEffect, useMemo, useState } from "react";
import { Building2, Check, ChevronDown, Coins, Crown, Flag, Flame, Home, ImagePlus, Landmark, Link2, Megaphone, Minus, Paintbrush, Plane, Plus, Sailboat, Send, Ship, Sparkles, Ticket, Trophy, UserRound, X } from "lucide-react";
import { shrink } from "@/lib/image";
import { card } from "./Notices";
import { Btn, Pill, Section, Uxd } from "./kit";
import { ACC, clock, dur, fmt } from "./util";

const ORIGIN = process.env.REACT_APP_BACKEND_URL;
const FIELD = "hud-well flex items-center gap-2 rounded-xl px-3 py-2 focus-within:ring-2 focus-within:ring-x-accent";
const INPUT = "min-w-0 flex-1 bg-transparent text-sm font-medium outline-none placeholder:text-x-mute";
const TIER_TIP = { prime: "Right by downtown", central: "A few blocks out", edge: "Out at the edge of town" };

// Uptown is rented by the week. The offer is the player's own lot, near their people.
export function Uptown({ econ, eco, now, onLot }) {
  const { rent, offer, uxd } = econ, here = rent ?? offer;
  const [leaving, setLeaving] = useState(false);
  const go = async (path, title) => {
    if ((await eco.act(path)) && title) card({ icon: Building2, tone: "green", title }, { duration: 3500 });
    setLeaving(false);
  };
  const due = rent && Date.parse(rent.due) - now;
  return (
    <Section id="uptown" icon={Building2} title="Uptown" accent="green" right={<Pill acc={rent ? "green" : "grey"}>{rent ? "Renting" : "On the Docks"}</Pill>}
      info={`Rent a lot uptown by the week; the closer to downtown, the more it costs. Rent comes out every ${econ.rent_days} days. Short on the day and you move back to the Docks. Your ships keep working either way.`}>
      <div className={`flex items-center gap-3 rounded-[20px] p-3 ${rent?.short ? "acc-card" : "border border-x-line"}`} style={{ "--acc": ACC[rent?.short ? "red" : "green"] }} data-testid="uptown-lot">
        <Building2 className="acc-text h-6 w-6 shrink-0" strokeWidth={2} />
        <div className="min-w-0 flex-1 leading-tight">
          <div className="text-sm font-semibold">{here.name} lot · <Uxd n={here.price} /> a week</div>
          <div className="text-xs tabular-nums text-x-mute">
            {!rent ? "Near your people. The first week is paid up front" : rent.short ? `${fmt(rent.price - uxd)} UXD short, due in ${dur(due)}` : `Next rent in ${dur(due)}`}
          </div>
        </div>
        {offer && <Btn tone="soft" onClick={() => onLot(offer.lot)} data-testid="uptown-look">Look</Btn>}
      </div>
      {offer ? (
        <Btn className="mt-2 w-full py-2.5" disabled={eco.busy || uxd < offer.price} onClick={() => go("/uptown/rent")} data-testid="uptown-rent">
          {uxd < offer.price ? `${fmt(offer.price - uxd)} UXD to go` : `Rent it · ${fmt(offer.price)} UXD`}
        </Btn>
      ) : leaving ? (
        <div className="mt-2 flex gap-2">
          <Btn tone="red" className="flex-1 py-2" disabled={eco.busy} onClick={() => go("/uptown/leave", "Back on the Docks")} data-testid="uptown-leave-confirm">Move out</Btn>
          <Btn tone="soft" className="flex-1 py-2" onClick={() => setLeaving(false)}>Stay</Btn>
        </div>
      ) : <Btn tone="soft" className="mt-2 w-full py-2" onClick={() => setLeaving(true)} data-testid="uptown-leave">Move back to the Docks</Btn>}
      <div className="mt-3 grid grid-cols-3 gap-2">
        {econ.rent_tiers.map((t) => (
          <div key={t.tier} title={TIER_TIP[t.tier]} className={`rounded-[10px] px-2 py-1.5 text-center ${t.tier === here.tier ? "acc-card" : "hud-well"}`} style={{ "--acc": ACC.green }}>
            <div className="text-xs font-semibold">{t.name}</div>
            <div className="text-[11px] tabular-nums text-x-mute">{fmt(t.price)}/wk</div>
          </div>
        ))}
      </div>
    </Section>
  );
}

const adState = (a, now) => (
  a.status === "pending" ? ["gold", "Waiting for approval"]
    : a.status === "approved" ? (Date.parse(a.start) > now ? ["blue", `Goes up ${clock(a.start)}`] : Date.parse(a.end) > now ? ["green", `Live until ${clock(a.end)}`] : ["grey", "Finished"])
      : a.status === "rejected" ? ["red", `Turned down: ${a.reason}`]
        : a.status === "ended" ? ["grey", `Taken down${a.refund ? `, ${fmt(a.refund)} back` : ""}`] : ["grey", "Cancelled"]);

function MyAd({ a, now, eco }) {
  const [acc, text] = adState(a, now);
  return (
    <li className="flex items-center gap-2.5 rounded-[14px] border border-x-line p-2" data-testid="my-ad">
      {a.image_url ? <img src={`${ORIGIN}${a.image_url}`} alt="" className="h-9 w-14 shrink-0 rounded-md object-cover" />
        : <span className="hud-well grid h-9 w-14 shrink-0 place-items-center rounded-md text-x-mute"><Megaphone className="h-4 w-4" /></span>}
      <div className="min-w-0 flex-1 leading-tight">
        <div className="truncate text-sm font-semibold">{a.slot_name}<span className="font-normal text-x-mute"> · {a.hours}h</span></div>
        <Pill acc={acc} className="mt-0.5 max-w-full truncate">{text}</Pill>
      </div>
      {a.status === "pending" && <Btn tone="soft" className="!px-2" disabled={eco.busy} onClick={() => eco.act(`/ads/${a.id}`, undefined, "DELETE")} aria-label="Cancel" title="Cancel and get it back"><X className="h-4 w-4" /></Btn>}
    </li>
  );
}

function AdForm({ s, econ, eco, onDone }) {
  const sign = s.id === "sign", max = sign ? 32 : 80;
  const [kind, setKind] = useState("url"), [file, setFile] = useState(null), [pic, setPic] = useState("");
  const [caption, setCaption] = useState(""), [link, setLink] = useState(""), [hours, setHours] = useState(1);
  const shown = useMemo(() => file && URL.createObjectURL(file), [file]);
  useEffect(() => () => shown && URL.revokeObjectURL(shown), [shown]);
  const left = econ.ad_hours_left, price = s.price * hours, ok = hours <= left && ( sign ? caption.trim() && link.trim() : kind === "profile" || (kind === "url" ? pic.trim() : file));
  const choose = (pic) => pic && shrink(pic).then((b) => new File([b], pic.name), () => pic).then(setFile);
  const send = async () => {
    const f = new FormData();
    Object.entries({ slot: s.id, hours, caption, link, kind: kind === "url" ? "image" : kind }).forEach(([k, v]) => f.append(k, v));
    if (!sign && kind === "image") f.append("image", file);
    if (!sign && kind === "url") f.append("image_link", pic.trim());
    if (!(await eco.act("/ads", f))) return;
    card({ icon: Megaphone, tone: "gold", title: "Sent for approval", amount: `-${fmt(price)}` }, { duration: 4000 });
    onDone();
  };
  return (
    <div className="space-y-2 px-1 pb-2 pt-1" data-testid="ad-form">
      {!sign && (
        <div className="hud-well flex gap-0.5 rounded-full p-0.5">
          {[["url", "Picture link", Link2], ["image", "Upload", ImagePlus], ["profile", "My profile", UserRound]].map(([id, label, Icon]) => (
            <button key={id} type="button" onClick={() => setKind(id)} aria-pressed={kind === id} data-testid={`ad-kind-${id}`}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-full py-1.5 text-xs font-semibold transition-colors ${kind === id ? "bg-x-surface text-x-ink" : "text-x-mute hover:text-x-ink"}`}>
              <Icon className="h-3.5 w-3.5" />{label}
            </button>
          ))}
        </div>
      )}
      {!sign && kind === "image" && (
        <label className={`${FIELD} cursor-pointer`} onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); choose(e.dataTransfer.files[0]); }}>
          {shown ? <img src={shown} alt="" className="h-8 w-8 shrink-0 rounded-md object-cover" data-testid="ad-preview" /> : <ImagePlus className="h-4 w-4 shrink-0 text-x-mute" />}
          <span className={`min-w-0 flex-1 truncate text-sm ${file ? "font-medium" : "text-x-mute"}`}>{file?.name ?? "Choose or drop a picture"}</span>
          <input type="file" accept="image/*" className="sr-only" onChange={(e) => choose(e.target.files[0])} data-testid="ad-file" />
        </label>
      )}
      {!sign && kind === "url" && (
        <label className={FIELD}>
          {pic.trim() ? <img src={pic.trim()} alt="" className="h-8 w-8 shrink-0 rounded-md object-cover" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
            onLoad={(e) => { e.currentTarget.style.visibility = "visible"; }} /> : <ImagePlus className="h-4 w-4 shrink-0 text-x-mute" />}
          <input value={pic} onChange={(e) => setPic(e.target.value)} maxLength={1000} placeholder="Image link, like i.imgur.com/….jpg" className={INPUT} data-testid="ad-image-link" />
        </label>
      )}
      <label className={FIELD}>
        <input value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={max} placeholder={sign ? "Your sign, a few words" : "Caption (optional)"} className={INPUT} data-testid="ad-caption" />
        <span className="text-[11px] tabular-nums text-x-mute">{max - caption.length}</span>
      </label>
      <label className={FIELD}>
        <Link2 className="h-4 w-4 shrink-0 text-x-mute" />
        <input value={link} onChange={(e) => setLink(e.target.value)} maxLength={200} placeholder={sign ? "Link, like x.com/you" : "Link (optional)"} className={INPUT} data-testid="ad-link" />
      </label>
      <div className="flex items-center gap-2">
        <div className="hud-well flex items-center rounded-full p-0.5">
          <button type="button" aria-label="Fewer hours" disabled={hours <= 1} onClick={() => setHours(hours - 1)} className="grid h-7 w-7 place-items-center rounded-full text-x-mute hover:bg-x-active disabled:opacity-40"><Minus className="h-3.5 w-3.5" /></button>
          <span className="w-16 text-center text-sm font-semibold tabular-nums" data-testid="ad-hours">{hours} {hours === 1 ? "hour" : "hours"}</span>
          <button type="button" aria-label="More hours" disabled={hours >= left} onClick={() => setHours(hours + 1)} className="grid h-7 w-7 place-items-center rounded-full text-x-mute hover:bg-x-active disabled:opacity-40"><Plus className="h-3.5 w-3.5" /></button>
        </div>
        <Btn className="flex flex-1 items-center justify-center gap-1.5 py-2" disabled={eco.busy || !ok || econ.uxd < price} onClick={send} data-testid="ad-send">
          <Send className="h-4 w-4" />{!left ? "No hours left" : hours > left ? `${left}h left` : econ.uxd < price ? `${fmt(price - econ.uxd)} UXD short` : `Send · ${fmt(price)}`}
        </Btn>
      </div>
    </div>
  );
}

// Billboards, the intercity train and a sign on your own house, by the hour. Paid now; the admin approves each one.
export function Advertise({ econ, eco, now }) {
  const [open, setOpen] = useState(null);
  return (
    <Section id="ads" icon={Megaphone} title="Advertise" accent="gold"
      right={<span className="text-xs tabular-nums text-x-mute" data-testid="ad-hours-left">{econ.ad_hours_left}h left</span>}
      info={`Put a picture (from a link or an upload) or your profile on a billboard or the intercity train, or a sign on your house, by the hour. Everyone gets ${econ.ad_max_hours} hours of ads at a time; time frees up as yours run. You pay now and the admin checks it; turned down, it all comes back.`}>
      {econ.ads.length > 0 && <ul className="mb-3 space-y-1.5">{econ.ads.map((a) => <MyAd key={a.id} a={a} now={now} eco={eco} />)}</ul>}
      <ul className="space-y-0.5" data-testid="ad-slots">
        {econ.ad_slots.map((s) => {
          const busyTill = s.next_free && Date.parse(s.next_free) > now;
          return (
            <li key={s.id} className={open === s.id ? "hud-well rounded-[14px]" : ""}>
              <button type="button" onClick={() => setOpen(open === s.id ? null : s.id)} aria-expanded={open === s.id} data-testid={`ad-slot-${s.id}`}
                className="flex w-full items-center gap-2 rounded-[14px] px-2.5 py-2 text-left transition-colors hover:bg-x-raised">
                <span className="min-w-0 flex-1 leading-tight">
                  <b className="block truncate text-sm font-semibold">{s.name}</b>
                  <span className="text-[11px] tabular-nums text-x-mute">{busyTill ? `Booked until ${clock(s.next_free)}` : "Free now"}</span>
                </span>
                <span className="text-sm font-semibold tabular-nums">{fmt(s.price)}<span className="text-[11px] font-medium text-x-mute">/hr</span></span>
                <ChevronDown className={`h-4 w-4 text-x-mute transition-transform ${open === s.id ? "rotate-180" : ""}`} />
              </button>
              {open === s.id && <AdForm key={s.id} s={s} econ={econ} eco={eco} onDone={() => setOpen(null)} />}
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

const PARTS = [["paint", "Walls", Paintbrush], ["roof", "Roof", Home], ["flag", "Flag", Flag], ["boat", "Boats", Sailboat]];
const Swatch = ({ c, on, onClick }) => (
  <button type="button" onClick={onClick} aria-label={c || "Plain"} aria-pressed={on} title={c || "Plain"}
    className={`grid aspect-square w-full place-items-center rounded-full border border-x-line ${c ? "" : "hud-well text-x-mute"}`}
    style={{ background: c || undefined, outline: on ? "2px solid var(--x-ink)" : "none", outlineOffset: 2 }}>
    {!c && <X className="h-3 w-3" />}
  </button>
);

// Permanent 3D luxury pieces on your building or moored in the harbour; each counts as invested so your worth stays whole.
const FLEX_ITEMS = [
  ["statue", "Golden statue", "On your lot", Trophy],
  ["helipad", "Rooftop helipad", "Copter on your roof", Plane],
  ["spire", "Golden spire", "Crown atop your roof", Crown],
  ["yacht", "Superyacht", "Moored in the harbour", Ship],
];

export function Luxury({ econ, eco, onBuy }) {
  const { owned = [], prices = {} } = econ.flex || {};
  const buy = async (id, label, cost) => {
    if (!(await eco.act("/me/flex", { item: id }))) return;
    onBuy();
    card({ icon: Crown, gold: true, title: `${label} added`, amount: `-${fmt(cost)}` }, { duration: 3500 });
  };
  return (
    <Section id="luxury" icon={Crown} title="Luxury" accent="gold"
      info="Permanent 3D pieces on your building or moored in the harbour. What you spend counts as invested, so your worth on the leaderboard stays whole.">
      <div className="grid grid-cols-2 gap-2" data-testid="luxury-grid">
        {FLEX_ITEMS.map(([id, title, sub, Icon]) => {
          const has = owned.includes(id), cost = prices[id] ?? 0;
          return (
            <div key={id} className="flex flex-col items-center gap-1 rounded-[20px] border border-x-line p-2.5 text-center" data-testid={`flex-${id}`}>
              <Icon className="h-6 w-6 text-x-gold" strokeWidth={2} />
              <b className="text-sm font-semibold leading-tight">{title}</b>
              <span className="text-xs text-x-mute">{sub}</span>
              {has ? <Pill acc="green" className="mt-1"><Check className="h-3 w-3" />Owned</Pill> : (
                <Btn tone="gold" className="mt-1 flex w-full items-center justify-center gap-1 !px-1 tabular-nums" disabled={eco.busy || econ.uxd < cost}
                  onClick={() => buy(id, title, cost)} data-testid={`flex-buy-${id}`}><Coins className="h-3.5 w-3.5" />{fmt(cost)}</Btn>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
}

// Put your @handle on a city landmark for 24 hours; anyone can outbid the current sponsor for 1.5x what they paid.
export function Landmarks({ econ, eco, now, onSponsored }) {
  const list = econ.landmarks || [];
  const sponsor = async (s) => {
    if (!(await eco.act(`/landmarks/${s.id}/sponsor`))) return;
    onSponsored();
    card({ icon: Landmark, gold: true, title: `Your name is on ${s.name}`, amount: `-${fmt(s.price)}` }, { duration: 3500 });
  };
  return (
    <Section id="landmarks" icon={Landmark} title="Landmarks" accent="gold"
      info={`Put your @handle on a city landmark for ${econ.landmark_hours ?? 24} hours. Anyone can outbid the current sponsor for 1.5× what they paid.`}>
      <ul className="space-y-1.5" data-testid="landmarks-list">
        {list.map((s) => {
          const h = s.holder, left = h ? Date.parse(h.until) - now : 0;
          return (
            <li key={s.id} className="flex items-center gap-2 rounded-[14px] border border-x-line px-3 py-2" data-testid={`landmark-${s.id}`}>
              <div className="min-w-0 flex-1 leading-tight">
                <b className="block truncate text-sm font-semibold">{s.name}</b>
                <span className="text-[11px] tabular-nums text-x-mute">{h && left > 0 ? `@${h.h} · ${dur(left)} left` : `Open · ${econ.landmark_hours ?? 24}h`}</span>
              </div>
              <Btn tone="gold" className="!py-1.5 text-xs tabular-nums" disabled={eco.busy || econ.uxd < s.price} onClick={() => sponsor(s)} data-testid={`landmark-buy-${s.id}`}>
                {h ? "Outbid" : "Sponsor"} · {fmt(s.price)}
              </Btn>
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

// Flies a golden blimp with your @handle over the city and boosts every ship payout in town while it's up.
export function Blimp({ econ, eco, now, onBlimp }) {
  const b = econ.blimp;
  if (!b) return null;
  const left = b.active ? Date.parse(b.active.until) - now : 0;
  const go = async () => {
    if (!(await eco.act("/city/blimp"))) return;
    onBlimp();
    card({ icon: Plane, gold: true, title: "Your golden blimp is in the sky", amount: `-${fmt(b.price)}` }, { duration: 3500 });
  };
  return (
    <Section id="blimp" icon={Plane} title="Golden blimp" accent="gold"
      right={left > 0 ? <Pill acc="gold">@{b.active.h} · {dur(left)}</Pill> : null}
      info={`Flies a golden blimp with your @handle over the city for ${b.min} minutes and boosts every ship payout in town by +${b.boost}%.`}>
      <Btn className="w-full py-2.5" disabled={eco.busy || econ.uxd < b.price} onClick={go} data-testid="blimp-buy">
        {econ.uxd < b.price ? `${fmt(b.price - econ.uxd)} UXD short` : `${left > 0 ? "Extend" : "Launch"} ${b.min}m (+${b.boost}% ships) · ${fmt(b.price)}`}
      </Btn>
    </Section>
  );
}

// Tickets for today's draw at midnight UTC; one ticket wins most of the pot and the rest leaves the city.
export function Lottery({ econ, eco, now }) {
  const l = econ.lottery;
  if (!l) return null;
  const draw = new Date(now);
  draw.setUTCHours(24, 0, 0, 0);
  const chance = l.mine && (l.mine / l.tickets) * 100;
  const buy = async (n) => {
    if (await eco.act("/lottery", { n })) card({ icon: Ticket, tone: "gold", title: `${fmt(n)} ticket${n > 1 ? "s" : ""} in today's draw`, amount: `-${fmt(n * l.price)}` }, { duration: 3000 });
  };
  const tile = (label, value) => (
    <div className="hud-well rounded-[10px] px-2 py-1.5 text-center">
      <div className="text-base font-semibold tabular-nums">{value}</div>
      <div className="text-[11px] text-x-mute">{label}</div>
    </div>
  );
  return (
    <Section id="lottery" icon={Ticket} title="Harbour Lottery" accent="gold" right={<Pill acc="gold">Draw in {dur(draw - now)}</Pill>}
      info={`Tickets are ${fmt(l.price)} UXD each. At midnight UTC one ticket is drawn and its owner takes the pot. The more tickets, the better your odds.`}>
      <div className="grid grid-cols-3 gap-2" data-testid="lottery">
        {tile("Pot", fmt(l.pot))}
        {tile(l.mine ? `${chance < 1 ? "<1" : Math.round(chance)}% to win` : "Your tickets", fmt(l.mine))}
        {tile("Players", fmt(l.players))}
      </div>
      <div className="mt-2 flex gap-2">
        {[1, 10, 100].map((n) => (
          <Btn key={n} tone={n === 1 ? "gold" : "soft"} className="flex-1 py-2 tabular-nums" disabled={eco.busy || econ.uxd < n * l.price} onClick={() => buy(n)} data-testid={`lottery-buy-${n}`}>
            {n}× · {fmt(n * l.price)}
          </Btn>
        ))}
      </div>
      {l.last && <p className="mt-2 text-xs text-x-mute">Last draw: @{l.last.h} won {fmt(l.last.uxd)} UXD</p>}
    </Section>
  );
}

// A show over your own house that everyone in town sees. Another one adds to the time left.
export function Fireworks({ econ, eco, now, onFireworks }) {
  const fw = econ.fireworks, left = fw.until ? Date.parse(fw.until) - now : 0;
  const go = async () => {
    if (!(await eco.act("/me/fireworks"))) return;
    onFireworks();
    card({ icon: Sparkles, gold: true, title: "Fireworks over your house", amount: `-${fmt(fw.price)}` }, { duration: 3500 });
  };
  return (
    <Section id="fireworks" icon={Flame} title="Fireworks" accent="gold" right={left > 0 ? <Pill acc="gold">{dur(left)} left</Pill> : null}
      info={`A ${fw.min}-minute show over your house that everyone in town sees, with your name on the ticker. Another one adds ${fw.min} minutes.`}>
      <Btn className="w-full py-2.5" disabled={eco.busy || econ.uxd < fw.price} onClick={go} data-testid="fireworks-buy">
        {econ.uxd < fw.price ? `${fmt(fw.price - econ.uxd)} UXD short` : `${left > 0 ? "Add" : "Set off"} ${fw.min} minutes · ${fmt(fw.price)}`}
      </Btn>
    </Section>
  );
}

// Colours for the house, its roof and flag and the boats at its dock. A change costs UXD; back to plain is free.
export function Looks({ econ, eco }) {
  const [pick, setPick] = useState({});
  const was = econ.looks, is = { ...was, ...pick };
  const changed = PARTS.filter(([p]) => (is[p] || "") !== (was[p] || ""));
  const cost = changed.reduce((n, [p]) => n + (is[p] ? econ.look_prices[p] : 0), 0);
  const save = async () => {
    if (!(await eco.act("/me/looks", Object.fromEntries(changed.map(([p]) => [p, is[p] || ""]))))) return;
    setPick({});
    card({ icon: Paintbrush, tone: "green", title: "Your house has a new look", amount: cost ? `-${fmt(cost)}` : null }, { duration: 3000 });
  };
  return (
    <Section id="looks" icon={Paintbrush} title="Looks" accent="blue" info="Paint your house, its roof and flag and the boats at your dock from the city palette. Each colour change costs UXD; back to plain is free.">
      <div className="space-y-2.5">
        {PARTS.map(([p, label, Icon]) => (
          <div key={p} data-testid={`looks-${p}`}>
            <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold"><Icon className="h-3.5 w-3.5 text-x-mute" />{label}<span className="ml-auto tabular-nums text-x-mute">{fmt(econ.look_prices[p])}</span></div>
            <div className="grid grid-cols-[repeat(15,minmax(0,1fr))] gap-1">
              <Swatch c="" on={!is[p]} onClick={() => setPick({ ...pick, [p]: "" })} />
              {econ.palette.map((c) => <Swatch key={c} c={c} on={is[p] === c} onClick={() => setPick({ ...pick, [p]: c })} />)}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        {changed.length > 0 && <Btn tone="soft" className="py-2" onClick={() => setPick({})}>Undo</Btn>}
        <Btn className="flex-1 py-2" disabled={!changed.length || eco.busy || econ.uxd < cost} onClick={save} data-testid="looks-save">
          {econ.uxd < cost ? `${fmt(cost - econ.uxd)} UXD short` : cost ? `Paint it · ${fmt(cost)}` : "Save"}
        </Btn>
      </div>
    </Section>
  );
}
